import { cn } from "~/lib/utils";

/**
 * Chain icon SVGs live in public/assets/chains/<variant>/<slug>.svg and are
 * vendored from web3icons (https://github.com/0xa3k5/web3icons) via
 * packages/tokenicons/assets/tokenicons/networks.
 *
 * Variants:
 * - "background": white logo on the brand-color square (best for small badges
 *   clipped to a circle — high contrast in both themes)
 * - "branded": full-color logo on a transparent background
 * - "mono": single-color glyph, rendered via CSS mask so it inherits
 *   `currentColor` and adapts to theme/context
 */
export type ChainIconVariant = "background" | "branded" | "mono";

export const CHAIN_ICON_SLUGS = [
  "arbitrum",
  "base",
  "blast",
  "bnb",
  "ethereum",
  "hyperevm",
  "linea",
  "monad",
  "optimism",
  "plume",
  "polygon",
  "robinhood",
  "solana",
  "unichain",
] as const;

export type ChainIconSlug = (typeof CHAIN_ICON_SLUGS)[number];

export const CHAIN_ICON_VARIANTS = [
  "background",
  "branded",
  "mono",
] as const satisfies readonly ChainIconVariant[];

const CHAIN_ID_TO_ICON_SLUG: Record<string, ChainIconSlug> = {
  "1": "ethereum",
  "10": "optimism",
  "56": "bnb",
  "130": "unichain",
  "137": "polygon",
  "143": "monad",
  "1399811149": "solana",
  "42161": "arbitrum",
  "59144": "linea",
  "8453": "base",
  "81457": "blast",
  "98866": "plume",
  "4663": "robinhood",
  "999": "hyperevm",
  solana: "solana",
};

const CHAIN_LABEL_KEYWORD_TO_ICON_SLUG: ReadonlyArray<
  readonly [string, ChainIconSlug]
> = [
  ["arbitrum", "arbitrum"],
  ["base", "base"],
  ["binance smart chain", "bnb"],
  ["blast", "blast"],
  ["bnb", "bnb"],
  ["bsc", "bnb"],
  ["ethereum", "ethereum"],
  ["hyperevm", "hyperevm"],
  ["linea", "linea"],
  ["monad", "monad"],
  ["optimism", "optimism"],
  ["plume", "plume"],
  ["polygon", "polygon"],
  ["robinhood", "robinhood"],
  ["solana", "solana"],
  ["unichain", "unichain"],
];

function normalizeChainIdForIcon(
  chainId: number | string | undefined,
): string | null {
  if (typeof chainId === "number" && Number.isFinite(chainId)) {
    return String(chainId);
  }

  if (typeof chainId !== "string") {
    return null;
  }

  const normalized = chainId.trim().toLowerCase();
  if (normalized.length === 0) {
    return null;
  }

  if (normalized.startsWith("eip155:")) {
    return normalized.slice("eip155:".length);
  }

  if (normalized === "solana:mainnet") {
    return "solana";
  }

  return normalized;
}

export function getChainIconSlug(
  chainId: number | string | undefined,
  chainLabel?: string | null,
): ChainIconSlug | null {
  const normalizedId = normalizeChainIdForIcon(chainId);
  if (normalizedId) {
    const slugById = CHAIN_ID_TO_ICON_SLUG[normalizedId];
    if (slugById) {
      return slugById;
    }
  }

  const normalizedLabel = chainLabel?.trim().toLowerCase();
  if (!normalizedLabel) {
    return null;
  }

  for (const [keyword, slug] of CHAIN_LABEL_KEYWORD_TO_ICON_SLUG) {
    if (normalizedLabel.includes(keyword)) {
      return slug;
    }
  }

  return null;
}

export function getChainIconUrl(
  slug: ChainIconSlug,
  variant: ChainIconVariant,
): string {
  return `/assets/chains/${variant}/${slug}.svg`;
}

export interface ChainIconProps {
  chainId?: number | string;
  chainLabel?: string;
  className?: string;
  /**
   * Hide from assistive tech — use when the chain name is already rendered
   * as adjacent text.
   */
  decorative?: boolean;
  /** Pixel size of the rendered icon. Defaults to 16. */
  size?: number;
  /** Accessible label; defaults to the resolved chain label. */
  title?: string;
  variant?: ChainIconVariant;
}

export function ChainIcon({
  chainId,
  chainLabel,
  className,
  decorative = false,
  size = 16,
  title,
  variant = "branded",
}: ChainIconProps) {
  const slug = getChainIconSlug(chainId, chainLabel);
  if (!slug) {
    return null;
  }

  const url = getChainIconUrl(slug, variant);
  const label = title ?? chainLabel ?? slug;

  if (variant === "mono") {
    const maskStyle = {
      maskImage: `url(${url})`,
      maskPosition: "center",
      maskRepeat: "no-repeat",
      maskSize: "contain",
    } as const;
    return (
      <span
        aria-hidden={decorative || undefined}
        aria-label={decorative ? undefined : label}
        className={cn("inline-block shrink-0 bg-current", className)}
        role={decorative ? undefined : "img"}
        style={{
          height: size,
          width: size,
          ...maskStyle,
          WebkitMaskImage: maskStyle.maskImage,
          WebkitMaskPosition: maskStyle.maskPosition,
          WebkitMaskRepeat: maskStyle.maskRepeat,
          WebkitMaskSize: maskStyle.maskSize,
        }}
        title={decorative ? undefined : label}
      />
    );
  }

  return (
    <img
      alt={decorative ? "" : label}
      aria-hidden={decorative || undefined}
      className={cn(
        "inline-block shrink-0",
        variant === "background" && "rounded-full",
        className,
      )}
      height={size}
      loading="lazy"
      src={url}
      style={{ height: size, width: size }}
      title={decorative ? undefined : label}
      width={size}
    />
  );
}
